import { useCallback } from 'react';
import { Flex, PrimaryButton } from '@banhala/ui-kit';
import { bridge, isNil } from 'shared/src/utils';
import type { GoodsOption } from 'shared/src/api';
import { useIsAnonymousUser } from 'shared/src/hooks';
import { systemAlert, useMemberSignupRequestModal } from 'shared/src/component';

import { useIsOnSale } from '../../../../hooks/useIsOnSale';
import { ImageBadgeFreeDelivery } from '../../../BottomDocked/components/ImageBadgeFreeDelivery';
import { usePostRestockNotification } from '../../queries/usePostRestockNotification';
import { useOnPressPurchase } from '../../hooks/useOnPressPurchase';
import { useOnPressAddToCart } from '../../hooks/useOnPressAddToCart';

export const InlineActionButtons = ({
  selected,
  isRestockMode,
  isPurchasable,
  isAlreadySubscribed,
  lastGoodsOptionSno,
  lastDepth,
  priorOptionSno,
  onCompleteRestockNotification,
  onComplete,
}: {
  selected: GoodsOption[];
  isRestockMode: boolean;
  isPurchasable: boolean;
  isAlreadySubscribed: boolean;
  lastGoodsOptionSno: number | undefined;
  lastDepth: number;
  priorOptionSno: number | undefined;
  onCompleteRestockNotification: () => void;
  onComplete: (action: 'checkout' | 'addToCart') => void;
}) => {
  const isAnonymousUser = useIsAnonymousUser();
  const isOnSaleNow = useIsOnSale();
  const { show: showMemberSignupRequestModal } = useMemberSignupRequestModal();

  const { mutateAsync: postRestockNotification, isPending: loadingRestock } =
    usePostRestockNotification(lastGoodsOptionSno ?? 0, {
      depth: lastDepth,
      selected_option_sno: priorOptionSno,
    });

  const onAnonymousUserPressedPurchase = useCallback(async () => {
    const result = await showMemberSignupRequestModal({
      title: '',
      description: '지금 가입하고\n더 많은 상품을 만나보세요',
    });
    if (result.type === 'confirm') {
      bridge.assertLogin({});
    }
  }, [showMemberSignupRequestModal]);

  const { onPressPurchase, loadingProductCheckout: loadingCheckout } =
    useOnPressPurchase({
      selected,
      onCompletePurchase: () => onComplete('checkout'),
      onAnonymouseUserPressedPurchase: onAnonymousUserPressedPurchase,
    });
  const { onPressAddToCart, loadingAddToCart } = useOnPressAddToCart({
    selected,
    onCompleteAddToCart: () => onComplete('addToCart'),
  });

  const onPressRestockNoti = useCallback(async () => {
    if (isNil(lastGoodsOptionSno)) return;
    if (isAnonymousUser) {
      bridge.assertLogin({});
      return;
    }
    if (isAlreadySubscribed) return;
    const shouldApply = await new Promise<boolean>(resolve => {
      systemAlert(
        '상품이 재입고 되면 푸시 알림으로 알려드릴게요. 재입고 알림을 받을까요?',
        undefined,
        [
          { text: '취소', onPress: () => resolve(false) },
          { text: '확인', onPress: () => resolve(true) },
        ],
      );
    });
    if (!shouldApply) return;
    await postRestockNotification();
    onCompleteRestockNotification();
    bridge.toast('재입고 알림 신청이 완료되었습니다.');
  }, [
    lastGoodsOptionSno,
    isAnonymousUser,
    isAlreadySubscribed,
    postRestockNotification,
    onCompleteRestockNotification,
  ]);

  if (isRestockMode) {
    return (
      <Flex
        height={80}
        padding={{ horizontal: 12, vertical: 12 }}
        borderTopWidth={1}
        borderColor={'border_tertiary'}
        backgroundColor={'bg_always_white'}
      >
        <PrimaryButton
          variant={'fill'}
          color={'blue'}
          outline={true}
          state={
            isAlreadySubscribed || loadingRestock ? 'disabled' : undefined
          }
          onPress={onPressRestockNoti}
          testID={'인라인재입고알림버튼'}
        >
          {isAlreadySubscribed ? '알림 신청 완료' : '재입고 알림 받기'}
        </PrimaryButton>
      </Flex>
    );
  }

  return (
    <Flex
      height={80}
      padding={{ horizontal: 12, vertical: 12 }}
      position={'relative'}
      borderTopWidth={1}
      borderColor={'border_tertiary'}
      backgroundColor={'bg_always_white'}
    >
      <Flex.Row>
        <Flex flex={1}>
          <PrimaryButton
            variant={'fill'}
            outline={true}
            state={isPurchasable && !loadingAddToCart ? undefined : 'disabled'}
            onPress={() => onPressAddToCart(false)}
            testID={'인라인장바구니버튼'}
          >
            장바구니
          </PrimaryButton>
        </Flex>
        <Flex flex={1} margin={{ left: 8 }}>
          <PrimaryButton
            variant={'fill'}
            state={isPurchasable && !loadingCheckout ? undefined : 'disabled'}
            onPress={onPressPurchase}
            testID={'인라인구매하기버튼'}
          >
            구매하기
          </PrimaryButton>
        </Flex>
      </Flex.Row>
      {isOnSaleNow && (
        <Flex position={'absolute'} top={5} right={20}>
          <ImageBadgeFreeDelivery />
        </Flex>
      )}
    </Flex>
  );
};
